import React, { useEffect, useMemo, useState } from "react";
import { Button, Dialog, Skeleton, Combobox, Tooltip } from "@/components/ui";
import { FormServices, FormDefault, UpdateFormDefaultsRequest, FormDefaultType } from "@/services/bedrock";
import { CoachService } from "@/services";
import { useSocialCoachSession } from "@/utils/useSocialCoachSession";
import { Branch, Region } from "@/interfaces";
import { toast } from "sonner";
import { AlertTriangle, Info } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { joinLabels, missingAccountDefaults } from "../formDefaults";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface OptionType {
  label: string;
  value: string;
}

const FormDefaultsDialog: React.FC<Props> = ({ open, onOpenChange }) => {
  const { coachId } = useSocialCoachSession();
  const queryClient = useQueryClient();

  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [branches, setBranches] = useState<Branch[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [defaults, setDefaults] = useState<FormDefault[]>([]);

  // 1. Fetch available forms to choose from
  const { data: formsData, isLoading: isLoadingForms } = useQuery({
    queryKey: ["forms", { page: 1, perPage: 100 }],
    queryFn: () => FormServices.getForms({ page: 1, perPage: 100, active: true }),
    enabled: open,
  });
  const formsList = formsData?.content || [];

  const formSelectItems = useMemo<OptionType[]>(() => {
    return formsList.map(form => ({
      label: form.displayName,
      value: String(form.id),
    }));
  }, [formsList]);

  // 2. Fetch branches, regions, and defaults on open
  useEffect(() => {
    if (!open || !coachId) return;

    setLoadingBranches(true);
    CoachService.branches(coachId)
      .then(response => {
        setBranches(response);
      })
      .catch(console.error)
      .finally(() => setLoadingBranches(false));

    setLoadingRegions(true);
    CoachService.regions(coachId)
      .then(response => {
        setRegions(response);
      })
      .catch(console.error)
      .finally(() => setLoadingRegions(false));

    setLoadingDefaults(true);
    FormServices.getCoachDefaults()
      .then(response => {
        setDefaults(response);
      })
      .catch(error => {
        console.error(error);
        toast.error("Error loading form defaults");
      })
      .finally(() => setLoadingDefaults(false));
  }, [open, coachId]);

  const handleSelectChange = (
    segmentationDetailId: string | undefined,
    formIdStr: string | undefined,
    formType: FormDefaultType
  ) => {
    setDefaults(list => {
      const targetId = segmentationDetailId ? Number(segmentationDetailId) : null;
      // Filter out any existing default for this category/segmentationDetailId and formType
      const others = list.filter(d => {
        if (targetId === null) {
          const isTargetDefault = d.segmentationDetailId === null || d.segmentationDetailId === undefined;
          return !(isTargetDefault && d.formType === formType);
        }
        return !(d.segmentationDetailId === targetId && d.formType === formType);
      });

      // If a valid form was selected, add it as the new default
      if (formIdStr && formIdStr !== "") {
        const selectedFormId = Number(formIdStr);
        const matchedForm = formsList.find(f => f.id === selectedFormId);
        if (!matchedForm) return list;

        const newDefault: FormDefault = {
          id: Date.now(), // temporary ID for React key
          formType,
          formId: selectedFormId,
          coachId: Number(coachId),
          segmentationDetailId: targetId as any,
          form: matchedForm,
        };
        return [newDefault, ...others];
      } else {
        return others;
      }
    });
  };

  const handleSave = () => {
    if (!coachId) return;

    const payload: UpdateFormDefaultsRequest = {
      defaults: defaults.map(d => ({
        formType: d.formType,
        formId: d.formId,
        segmentationDetailId: d.segmentationDetailId ? Number(d.segmentationDetailId) : undefined,
      })),
    };

    setIsSaving(true);
    FormServices.updateCoachDefaults(payload)
      .then(() => {
        toast.success("Form defaults updated successfully!");
        queryClient.invalidateQueries({ queryKey: ["formDefaults"] });
        onOpenChange(false);
      })
      .catch(error => {
        console.error(error);
        toast.error("Error saving form defaults");
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const renderFormDropdown = (segmentationDetailId: number | string | undefined, formType: FormDefaultType) => {
    const targetId = segmentationDetailId ? Number(segmentationDetailId) : null;
    const currentDefault = defaults.find(d => {
      if (targetId === null) {
        return (d.segmentationDetailId === null || d.segmentationDetailId === undefined) && d.formType === formType;
      }
      return d.segmentationDetailId === targetId && d.formType === formType;
    });

    const currentValue = currentDefault ? String(currentDefault.formId) : "";
    // An empty account default leaves players without a region or branch default, and links with no
    // player, with no form to open, so it's marked until it's filled
    const needed = targetId === null && !currentDefault;

    return (
      <div className="w-full select-text flex flex-col gap-1">
        <Combobox
          items={formSelectItems}
          value={formSelectItems.find(i => i.value === currentValue) || null}
          onValueChange={(val: OptionType | null) => {
            handleSelectChange(
              segmentationDetailId ? String(segmentationDetailId) : undefined,
              val ? val.value : undefined,
              formType
            );
          }}
          searchable
          showClear
          placeholder="None selected"
          emptyText="No forms found"
          isItemEqualToValue={(current, selected) => current?.value === selected?.value}
          itemToStringLabel={item => item?.label || ""}
          className={cn(
            "text-xs font-semibold text-slate-700 border w-full flex items-center justify-between h-9 rounded-lg transition-colors cursor-pointer outline-none",
            needed
              ? "bg-amber-50 border-amber-300 hover:bg-amber-100"
              : "bg-slate-50 border-slate-200 hover:bg-slate-100"
          )}
        />
        {needed && <span className="text-[11px] font-medium text-amber-700">Choose a form</span>}
      </div>
    );
  };

  const isInitializing = loadingBranches || loadingRegions || loadingDefaults || isLoadingForms;
  // From the dialog's working copy, so the warning clears as each one is picked
  const missingDefaults = missingAccountDefaults(defaults);

  return (
    <Dialog open={open} size="3xl" className="top-16 translate-y-0" onOpenChange={onOpenChange}>
      <div className="flex flex-col">
        <Dialog.Header>
          <Dialog.Title>Form Defaults Editor</Dialog.Title>
        </Dialog.Header>
        <Dialog.Content className="max-h-[60vh] overflow-y-auto pr-1 flex flex-col gap-4">
          {/* Explanation Section */}
          {!isInitializing && missingDefaults.length > 0 ? (
            <div
              className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 text-sm text-amber-900 leading-relaxed text-left select-none"
              data-testid="missing-account-defaults"
            >
              <AlertTriangle className="size-5 shrink-0 text-amber-600 mt-0.5" />
              <div className="flex flex-col gap-1">
                <span className="font-bold text-amber-950">Set an account default for each type</span>
                <span>
                  The account default is the fallback: it's the form for any player whose region or branch doesn't have
                  its own, and for links with no player, like Form Routes. Without one, those players and links have no
                  form to open.
                </span>
                <span className="font-semibold">Missing: {joinLabels(missingDefaults.map(d => d.label))}.</span>
              </div>
            </div>
          ) : (
            <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-4 flex gap-3 text-sm text-indigo-800 leading-relaxed text-left select-none animate-in fade-in duration-300">
              <Info className="size-5 shrink-0 text-indigo-600 mt-0.5" />
              <div className="flex flex-col gap-3">
                <div>
                  <span className="font-bold text-indigo-950 mb-0.5 block">How Form Defaults Work</span>
                  <span className="text-indigo-900/95">
                    Set default forms for your players by region, branch, or account to minimize manual steps. Players
                    can still choose different forms manually if needed.
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Table defaults view */}
          <div className="w-full min-w-md select-none">
            {isInitializing ? (
              <div className="flex flex-col gap-3">
                <Skeleton className="w-full h-12 rounded-lg" />
                <Skeleton className="w-full h-24 rounded-lg" />
                <Skeleton className="w-full h-24 rounded-lg" />
              </div>
            ) : (
              <div className="flex flex-col w-full mt-1">
                {/* Unified single table with full-width header rows */}
                <table className="w-full border-collapse border border-slate-200 rounded-lg overflow-hidden text-sm shadow-sm">
                  <thead className="bg-slate-50 text-slate-700 font-bold border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left w-1/4">Segmentation Category</th>
                      <th className="px-4 py-3 text-left w-1/4">
                        <div className="flex items-center gap-1.5">
                          <span>Review Default</span>
                          <Tooltip>
                            <Tooltip.Trigger
                              render={
                                <Info className="size-4 text-slate-400 hover:text-slate-600 transition-colors cursor-help" />
                              }
                            />
                            <Tooltip.Content className="max-w-xs whitespace-normal font-normal">
                              Applied automatically when players share standard review links, allowing their clients to
                              quickly leave public/social reviews.
                            </Tooltip.Content>
                          </Tooltip>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left w-1/4">
                        <div className="flex items-center gap-1.5">
                          <span>Mid-transaction check-in</span>
                          <Tooltip>
                            <Tooltip.Trigger
                              render={
                                <Info className="size-4 text-slate-400 hover:text-slate-600 transition-colors cursor-help" />
                              }
                            />
                            <Tooltip.Content className="max-w-xs whitespace-normal font-normal">
                              Used by Form Routes when a client is asked for feedback part-way through a transaction.
                            </Tooltip.Content>
                          </Tooltip>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left w-1/4">
                        <div className="flex items-center gap-1.5">
                          <span>Bad Review Feedback Default</span>
                          <Tooltip>
                            <Tooltip.Trigger
                              render={
                                <Info className="size-4 text-slate-400 hover:text-slate-600 transition-colors cursor-help" />
                              }
                            />
                            <Tooltip.Content className="max-w-xs whitespace-normal font-normal">
                              Used when Players reach out for private feedback after receiving a bad review (e.g., when
                              sending a personalized Video Page), allowing you to gather more information internally
                              about how to improve.
                            </Tooltip.Content>
                          </Tooltip>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white text-slate-800 divide-y divide-slate-100">
                    {/* 1. Account Level Default */}
                    <tr>
                      <td className="px-4 py-3 text-left font-semibold text-slate-700">Account Default</td>
                      <td className="px-4 py-2 text-left">{renderFormDropdown(undefined, "review")}</td>
                      <td className="px-4 py-2 text-left">{renderFormDropdown(undefined, "midpoint")}</td>
                      <td className="px-4 py-2 text-left">{renderFormDropdown(undefined, "bad_review_feedback")}</td>
                    </tr>

                    {/* 2. Region Section Divider Row */}
                    {regions.length > 0 && (
                      <>
                        <tr className="bg-slate-50 border-y border-slate-200 select-none">
                          <td
                            colSpan={4}
                            className="px-4 py-2 text-left text-[11px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50/50"
                          >
                            Region Defaults
                          </td>
                        </tr>
                        {regions.map(region => (
                          <tr key={`region-default-${region.regionId}`}>
                            <td className="px-4 py-3 text-left font-medium text-slate-600 pl-8">{region.name}</td>
                            <td className="px-4 py-2 text-left">{renderFormDropdown(region.regionId, "review")}</td>
                            <td className="px-4 py-2 text-left">{renderFormDropdown(region.regionId, "midpoint")}</td>
                            <td className="px-4 py-2 text-left">
                              {renderFormDropdown(region.regionId, "bad_review_feedback")}
                            </td>
                          </tr>
                        ))}
                      </>
                    )}

                    {/* 3. Branch Section Divider Row */}
                    {branches.length > 0 && (
                      <>
                        <tr className="bg-slate-50 border-y border-slate-200 select-none">
                          <td
                            colSpan={4}
                            className="px-4 py-2 text-left text-[11px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50/50"
                          >
                            Branch Defaults
                          </td>
                        </tr>
                        {branches.map(branch => (
                          <tr key={`branch-default-${branch.branchId}`}>
                            <td className="px-4 py-3 text-left font-medium text-slate-600 pl-8">{branch.name}</td>
                            <td className="px-4 py-2 text-left">{renderFormDropdown(branch.branchId, "review")}</td>
                            <td className="px-4 py-2 text-left">{renderFormDropdown(branch.branchId, "midpoint")}</td>
                            <td className="px-4 py-2 text-left">
                              {renderFormDropdown(branch.branchId, "bad_review_feedback")}
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Dialog.Content>
        <Dialog.Footer>
          <div className="flex justify-end items-center gap-2 select-none">
            <Button type="button" variant="outline" disabled={isSaving} onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={isInitializing} loading={isSaving} onClick={handleSave}>
              Save
            </Button>
          </div>
        </Dialog.Footer>
      </div>
    </Dialog>
  );
};

export { FormDefaultsDialog };
